import React from "react";
import { Link } from "react-router-dom";
import MainFooter from "../shared/MainFooter.jsx";
import { LEGAL_COMPANY } from "@/lib/legalCompany";

const LegalPageShell = ({ title, children }) => (
  <div className="choice-landing min-h-screen bg-[#080b14] noise-overlay">
    <header className="border-b border-white/[0.05]">
      <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img src="/images/logo.png" alt={LEGAL_COMPANY.brandName} className="w-full max-w-[100px]" />
        </Link>
        <Link to="/" className="text-sm text-gray-400 hover:text-white transition-colors">
          ← Back to home
        </Link>
      </div>
    </header>

    <main className="max-w-3xl mx-auto px-6 py-16">
      <h1 className="text-3xl md:text-4xl font-semibold text-white mb-8">{title}</h1>
      <div className="prose prose-invert prose-sm max-w-none text-gray-400 space-y-6">{children}</div>
    </main>

    <MainFooter />
  </div>
);

const linkClass = "text-teal-400 hover:text-teal-300";

const TermsPage = () => (
  <LegalPageShell title="Terms of Service">
    <p className="text-gray-500 text-sm">Last updated: August 2026</p>

    <section className="space-y-4">
      <p>
        Welcome to {LEGAL_COMPANY.legalName} (&quot;{LEGAL_COMPANY.brandName},&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;). These Terms
        of Service (&quot;Terms&quot;) govern your access to and use of the {LEGAL_COMPANY.brandName} website, AI-powered digital
        employee software, and related services (collectively, the &quot;Service&quot;). By creating an account,
        clicking to accept these Terms, or using our Service, you agree to be bound by these Terms, our{" "}
        <Link to="/privacy-policy" className={linkClass}>
          Privacy Policy
        </Link>
        , and our{" "}
        <Link to="/dpa" className={linkClass}>
          Data Processing Agreement (&quot;DPA&quot;)
        </Link>
        , which together constitute the entire agreement governing your use of the Service.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">1. Definitions</h2>
      <p>For purposes of these Terms:</p>
      <ul className="list-disc pl-5 space-y-2">
        <li>
          <strong className="text-white">&quot;Account&quot;</strong> means the customer account registered to access the
          Service.
        </li>
        <li>
          <strong className="text-white">&quot;Beta Feature&quot;</strong> means any feature or service marked as
          &quot;Beta,&quot; &quot;Early Access,&quot; or similar.
        </li>
        <li>
          <strong className="text-white">&quot;Confidential Information&quot;</strong> means non-public business,
          technical, or financial information disclosed by one party to the other in connection with the Service.
        </li>
        <li>
          <strong className="text-white">&quot;Customer,&quot; &quot;you,&quot; or &quot;your&quot;</strong> means the
          individual or entity that accepts these Terms and uses the Service.
        </li>
        <li>
          <strong className="text-white">&quot;Customer Content&quot;</strong> means content, messages, prompts, scripts,
          configurations, and materials you submit, upload, configure, or cause the Service to generate or send on your
          behalf (including outbound SMS, email, and chat content).
        </li>
        <li>
          <strong className="text-white">&quot;Feedback&quot;</strong> means suggestions, ideas, feature requests, or other
          feedback you provide regarding the Service.
        </li>
        <li>
          <strong className="text-white">&quot;Order Form&quot;</strong> means a signed order form, statement of work, or
          similar commercial ordering document between you and {LEGAL_COMPANY.brandName}.
        </li>
        <li>
          <strong className="text-white">&quot;Personal Data&quot;</strong> means personal information or personal data as
          defined under applicable privacy laws.
        </li>
        <li>
          <strong className="text-white">&quot;Privacy Policy&quot;</strong> means {LEGAL_COMPANY.brandName}&apos; Privacy Policy
          available at /privacy-policy, as updated from time to time.
        </li>
        <li>
          <strong className="text-white">&quot;DPA&quot;</strong> means {LEGAL_COMPANY.brandName}&apos; Data Processing Agreement
          available at /dpa, as updated from time to time, and any executed copy between you and {LEGAL_COMPANY.brandName}.
        </li>
        <li>
          <strong className="text-white">&quot;Service&quot;</strong> means the {LEGAL_COMPANY.brandName} website, AI-powered digital
          employee software, and related services.
        </li>
        <li>
          <strong className="text-white">&quot;Subprocessor&quot;</strong> means a third party engaged by {LEGAL_COMPANY.brandName} to
          process Personal Data or User Data to help operate the Service.
        </li>
        <li>
          <strong className="text-white">&quot;User Data&quot;</strong> means lead data, customer lists, and other input
          data you upload to or collect through the Service (excluding {LEGAL_COMPANY.brandName}&apos; underlying Service, models,
          prompts, and workflows).
        </li>
      </ul>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">2. Acceptance of Terms, Age Requirement &amp; B2B Declaration</h2>
      <p>
        By accessing or using the Service, you confirm that you can form a binding contract with {LEGAL_COMPANY.legalName} and
        that you accept these Terms.
      </p>
      <p>
        <strong className="text-white">Business Use:</strong> The Service is offered for commercial, business, and
        professional use. You represent that you are acquiring and using the Service for business purposes and not for
        personal, family, or household purposes. Nothing in these Terms excludes, restricts, or modifies any rights or
        remedies that cannot be excluded, restricted, or modified under applicable law, including non-excludable rights
        under the Australian Consumer Law (&quot;ACL&quot;), the New Zealand Consumer Guarantees Act 1993 (&quot;CGA&quot;),
        and unfair contract terms regimes.
      </p>
      <p>
        <strong className="text-white">Age Requirement:</strong> You must be at least 18 years old, or the age of legal
        majority in your jurisdiction, to create an Account and use the Service.
      </p>
      <p>
        <strong className="text-white">Corporate Authority:</strong> If you are using the Service on behalf of a company
        or organization, you represent that you have the authority to bind that entity to these Terms, and
        &quot;Customer&quot; refers to that entity.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">3. Description of Service, AI Limitations &amp; Beta Features</h2>
      <p>
        {LEGAL_COMPANY.brandName} provides AI-powered automation tools, including lead intercept protocols, automated communication (SMS,
        email, chat), and calendar booking systems.
      </p>
      <p>
        <strong className="text-white">AI Limitations &amp; Commitments:</strong> While our AI is designed for high
        performance, you acknowledge that AI-generated outputs may occasionally be inaccurate, incomplete, or require
        human review. You remain solely responsible for any business decisions, communications, and actions taken based on
        the Service. {LEGAL_COMPANY.brandName} is not liable for any unauthorized commitments, discounts, or contractual offers
        mistakenly generated by the AI to your leads or customers.
      </p>
      <p>
        <strong className="text-white">High-Risk Outputs:</strong> You must ensure that a qualified human reviews
        AI-generated outputs involving pricing, insurance, legal, medical, financial, or other regulated matters before
        they are acted upon or communicated to third parties.
      </p>
      <p>
        <strong className="text-white">Regulated Advice:</strong> You must not rely on the Service as your sole source of
        legal, medical, financial, or other regulated professional advice. Any AI-generated content in these domains
        must be reviewed and approved by a qualified human professional before being acted upon or communicated to third
        parties.
      </p>
      <p>
        <strong className="text-white">Customer Content &amp; AI Output Ownership:</strong> You retain ownership of
        Customer Content and of AI-generated outputs produced through your use of the Service, subject to {LEGAL_COMPANY.brandName}
        retaining all rights in the underlying Service, models, prompts, and workflows. You grant {LEGAL_COMPANY.brandName} a
        worldwide, non-exclusive licence to host, store, transmit, process, and display Customer Content and such outputs
        solely to provide, maintain, secure, and improve the Service for you. For clarity, &quot;improve&quot; does not
        include using your User Data or Customer Content to train public or third-party foundation models, consistent with
        our Privacy Policy and our then-current provider restrictions.
      </p>
      <p>
        <strong className="text-white">Third-Party AI Providers:</strong> We utilize enterprise-grade AI APIs (e.g.,
        OpenAI, Anthropic). Under our then-current provider agreements, we contractually restrict third-party AI providers
        from using your User Data, Customer Content, chat logs, customer interactions, or lead information to train
        their public or proprietary AI models, consistent with our{" "}
        <Link to="/privacy-policy" className={linkClass}>
          Privacy Policy
        </Link>
        .
      </p>
      <p>
        <strong className="text-white">Performance &amp; Response Times:</strong> Any performance metrics, speeds, or
        response times (e.g., &quot;26-second response&quot;) mentioned in our marketing materials, websites, or case
        studies are historical averages, estimates, or optimal targets. Because the Service relies on third-party AI
        models and external network conditions, we cannot and do not guarantee any specific delivery times, response
        speeds, or latency metrics.
      </p>
      <p>
        <strong className="text-white">Beta Features:</strong> We may occasionally offer Beta Features. These features are
        provided &quot;AS IS&quot; without any warranties, may contain bugs, and can be modified, suspended, or
        discontinued at any time without prior notice.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">4. Account Registration &amp; Security</h2>
      <p>
        To use our Service, you must register for an Account. You agree to provide accurate and complete information. You
        are solely responsible for maintaining the confidentiality of your Account credentials and API keys, and you
        must not share credentials with unauthorized persons. You must promptly notify {LEGAL_COMPANY.brandName} of any suspected
        unauthorized access to your Account. {LEGAL_COMPANY.brandName} is not liable for any loss or damage arising from unauthorized
        access to your Account resulting from your failure to safeguard credentials, except to the extent caused by
        {LEGAL_COMPANY.brandName}&apos; failure to implement appropriate security measures as required by applicable law.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">
        5. Subscription, Billing, Payment Processing, Auto-Renewal &amp; Cancellation
      </h2>
      <p>
        <strong className="text-white">Billing &amp; Auto-Renewal:</strong> The Service is billed on a subscription basis
        in United States Dollars (USD), unless otherwise specified in a signed Order Form. Your subscription will
        automatically renew at the end of each billing cycle at the then-current rate unless you cancel before the
        renewal date. By subscribing, you authorize {LEGAL_COMPANY.brandName} to charge your selected payment method for all applicable
        fees upon initial purchase and each subsequent renewal. We will provide clear disclosure at signup (and in your
        Account settings) of the renewal terms, including the renewal price, billing frequency, auto-renewal, and any
        trial-to-paid conversion terms.
      </p>
      <p>
        <strong className="text-white">Price Changes:</strong> {LEGAL_COMPANY.brandName} may change subscription fees upon at least 30
        days&apos; advance notice via email or in-product notification. If you do not agree to a price change, you may
        cancel your subscription before the change takes effect. If you do not cancel before the effective date after
        receiving such notice, the modified fees will apply to subsequent renewal periods.
      </p>
      <p>
        <strong className="text-white">Payment Processing:</strong> We use third-party payment processors (e.g., Stripe)
        to process billing for the Service. We do not store your full payment card credentials on our servers. Processing
        of payments is subject to the terms and privacy policies of the payment processor in addition to these Terms.
      </p>
      <p>
        <strong className="text-white">Non-Payment:</strong> If payment fails, we may suspend or terminate your access to
        the Service after providing notice and a reasonable opportunity to update payment details, except where
        immediate suspension is reasonably necessary to prevent abuse or legal risk.
      </p>
      <p>
        <strong className="text-white">Cancellation:</strong> You may cancel your subscription at any time through your
        Account settings or by emailing {LEGAL_COMPANY.supportEmail}. Cancellation should be no more difficult than signup. We
        will confirm cancellation by email where practicable. Cancellation takes effect at the end of the current
        billing period; you will retain access until that date. No further charges will be applied after cancellation is
        processed for subsequent periods.
      </p>
      <p>
        <strong className="text-white">Refunds:</strong> Unless otherwise required by applicable law (including the ACL
        and the CGA), all payments are non-refundable, and we do not provide refunds or credits for partial subscription
        periods, including mid-cycle downgrades.
      </p>
      <p>
        <strong className="text-white">Chargebacks:</strong> If you initiate a chargeback or payment dispute that is
        determined not to be bona fide, you remain liable for the disputed fees plus any reasonable costs incurred by
        {LEGAL_COMPANY.brandName} in resolving the dispute.
      </p>
      <p>
        <strong className="text-white">Free Trial:</strong> If we offer a free trial, it is provided &quot;AS IS&quot;
        without warranties (except non-excludable rights). At the end of the trial period, your Account may automatically
        convert to a paid subscription at the rate disclosed at signup unless you cancel before the trial ends. Trial
        terms will be clearly disclosed at the time of registration.
      </p>
      <p>
        <strong className="text-white">Taxes:</strong> All fees are exclusive of applicable national, state, or local
        taxes (including Sales Tax, Goods and Services Tax (GST), or VAT), unless an Order Form states otherwise. Where
        GST is payable on a taxable supply, {LEGAL_COMPANY.brandName} will issue a tax invoice. You are responsible for paying all taxes
        associated with your purchases, except taxes based on {LEGAL_COMPANY.brandName}&apos; net income. You must provide billing
        details reasonably requested by {LEGAL_COMPANY.brandName} (including your ABN if you are registered for GST in Australia).
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">6. Acceptable Use, Legal Compliance, A2P 10DLC &amp; Export Control</h2>
      <p>Because the Service involves automated communication with your leads, you are solely responsible for complying with all applicable laws.</p>
      <p>
        <strong className="text-white">Consent &amp; Anti-Spam Compliance:</strong> You explicitly guarantee that you have
        obtained the necessary legal consent (opt-in) from your leads and customers before using {LEGAL_COMPANY.brandName} to send
        automated emails, SMS messages, or other communications. You agree to comply with all applicable communication
        laws, including but not limited to the TCPA (Telephone Consumer Protection Act), CAN-SPAM Act, GDPR, the
        Australian Spam Act 2003, and the New Zealand Unsolicited Electronic Messages Act 2007.
      </p>
      <p>
        <strong className="text-white">Do Not Call &amp; Telemarketing Compliance:</strong> You agree to comply with the
        U.S. National Do Not Call Registry, the Australian Do Not Call Register Act 2006, and applicable telemarketing
        regulations in all jurisdictions where you operate.
      </p>
      <p>
        <strong className="text-white">SMS Compliance &amp; A2P 10DLC (US Users):</strong> If you use the Service to send
        SMS messages to U.S. numbers, you are solely responsible for registering your brand and campaigns with the A2P
        10DLC registry and complying with CTIA Messaging Principles and carrier guidelines. {LEGAL_COMPANY.brandName} is not liable
        for any message filtering, blocking, or penalties imposed by mobile carriers due to your failure to comply with
        A2P 10DLC regulations.
      </p>
      <p>
        <strong className="text-white">Outbound Content Responsibility:</strong> You are solely responsible for the content
        of all outbound messages and Customer Content, including for defamation, misleading or deceptive claims,
        unlicensed professional advice, and any commitments made to your leads or customers.
      </p>
      <p>
        <strong className="text-white">Platform Role &amp; Sender of Record:</strong> You are the sender of all
        communications to your leads and customers sent through the Service. {LEGAL_COMPANY.brandName} provides the Service as a
        technology provider and data processor acting on your instructions and configurations. {LEGAL_COMPANY.brandName} does not
        independently initiate marketing or outreach campaigns to your leads.
      </p>
      <p>
        <strong className="text-white">Prohibited Industries:</strong> You must not use the Service in connection with
        unlawful gambling, illegal substances, fraud schemes, phishing, or other illegal or high-risk messaging use
        cases prohibited by applicable law or mobile carrier policies.
      </p>
      <p>
        <strong className="text-white">Export &amp; Sanctions Compliance:</strong> You represent and warrant that you are
        not located in a country that is subject to a U.S. Government embargo or that has been designated by the U.S.
        Government as a &quot;terrorist supporting&quot; country; that you are not listed on any U.S. Government list of
        prohibited or restricted parties; and that you are not a sanctioned or prohibited person under Australian DFAT /
        autonomous sanctions regimes or applicable New Zealand sanctions laws. You will not use the Service in
        violation of applicable export control or sanctions laws.
      </p>
      <p>
        <strong className="text-white">Prohibited Use:</strong> You agree not to use the Service to: (a) send spam or
        unsolicited communications lacking required consent; (b) distribute malware, phishing, or engage in
        harassment; (c) impersonate others unlawfully, or present AI-generated communications as human where applicable
        law requires disclosure that the communication is automated or AI-assisted; (d) engage in any unlawful
        activities; (e) attempt to reverse engineer, decompile, or extract the underlying AI models, system prompts, or
        algorithms of the Service; or (f) use the Service to build a competing product in a manner that misappropriates
        {LEGAL_COMPANY.brandName}&apos; Confidential Information or proprietary materials. {LEGAL_COMPANY.brandName} may suspend or terminate your
        Account for violations of these rules, subject to Section 13 (Termination).
      </p>
      <p>
        <strong className="text-white">Fair Use Policy &amp; Rate Limits:</strong> To ensure optimal performance for all
        users and manage AI processing overhead, {LEGAL_COMPANY.brandName} may enforce rate limits on API calls, message volumes, or
        automation triggers based on your subscription plan and published platform thresholds. If your usage
        significantly exceeds the limits applicable to your plan, {LEGAL_COMPANY.brandName} may temporarily throttle your usage, require
        you to transition to a higher tier, or suspend access. Where practicable (and except for abuse, security, or
        illegal activity), we will provide notice before suspension for fair-use violations.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">7. Intellectual Property, Data Ownership, Backups, DPA &amp; Publicity</h2>
      <p>
        <strong className="text-white">{LEGAL_COMPANY.brandName} IP:</strong> {LEGAL_COMPANY.brandName} retains all rights, title, and interest in and
        to the Service, including the underlying algorithms, software, AI prompts, workflows, and website design.
      </p>
      <p>
        <strong className="text-white">Your Data:</strong> You retain all ownership rights to User Data. You grant
        {LEGAL_COMPANY.brandName} a licence to process User Data solely for the purpose of providing the Service to you. We do not use
        your private lead data to train public AI models.
      </p>
      <p>
        <strong className="text-white">Data Controller / Processor Roles:</strong> For lead data and end-user
        information processed through the Service, you act as the data controller (or equivalent) and {LEGAL_COMPANY.brandName} acts
        as a data processor (or service provider), processing such data strictly as instructed by you. You are responsible
        for ensuring your own privacy policies and consents are in place for lead capture.
      </p>
      <p>
        <strong className="text-white">Data Backups:</strong> While we take reasonable measures to protect your data, you
        are solely responsible for maintaining independent backups of your User Data and lead information. {LEGAL_COMPANY.brandName} is
        not liable for data loss caused by third-party server outages (e.g., AWS) or accidental deletion by you, except
        to the extent such loss is caused by {LEGAL_COMPANY.brandName}&apos; failure to implement appropriate safeguards required by
        applicable law.
      </p>
      <p>
        <strong className="text-white">Data Export &amp; Deletion:</strong> Upon cancellation or termination of your Account,
        you may request an export of your User Data by emailing {LEGAL_COMPANY.supportEmail} within thirty (30) days after the
        effective cancellation or termination date (or until deletion is completed, if earlier). {LEGAL_COMPANY.brandName} will provide
        the export in a reasonable common format within a reasonable time. After that period, {LEGAL_COMPANY.brandName} may permanently
        delete your User Data in the normal course of operation, subject to the retention exceptions below. For accounts with
        90 days of complete inactivity or non-payment, {LEGAL_COMPANY.brandName} will send a prior email notice to your Account email
        address before deletion, providing a reasonable opportunity to reactivate or request an export of your data. Deleted
        data cannot be recovered once deletion is completed.
      </p>
      <p>
        <strong className="text-white">Data Retention Exceptions:</strong> Notwithstanding the above, {LEGAL_COMPANY.brandName} may retain
        certain data after Account termination for: (a) billing and payment records as required by law; (b) legal hold
        or compliance obligations; (c) security and audit logs; and (d) backup systems, which are purged on a rolling
        basis within 30 to 90 days.
      </p>
      <p>
        <strong className="text-white">Subprocessors:</strong> {LEGAL_COMPANY.brandName} uses trusted third-party Subprocessors to
        operate the Service, including cloud hosting providers (e.g., AWS), payment processors (e.g., Stripe),
        enterprise AI model providers (e.g., OpenAI, Anthropic), and CRM or communications integrations as configured by
        you. All Subprocessors are subject to confidentiality and data protection obligations consistent with these Terms,
        our Privacy Policy, and the DPA. A current illustrative list of Subprocessors is set out in the{" "}
        <Link to="/dpa" className={linkClass}>
          DPA
        </Link>{" "}
        and is also available on request by emailing {LEGAL_COMPANY.supportEmail}.
      </p>
      <p>
        <strong className="text-white">Privacy Policy:</strong> Our{" "}
        <Link to="/privacy-policy" className={linkClass}>
          Privacy Policy
        </Link>{" "}
        is incorporated into these Terms by reference and governs how we collect, use, and protect personal
        information.
      </p>
      <p>
        <strong className="text-white">Data Processing Agreement (DPA):</strong> To the extent that {LEGAL_COMPANY.brandName} processes
        Personal Data on your behalf subject to the GDPR, CCPA/CPRA, Australian Privacy Act 1988, New Zealand Privacy
        Act 2020, or similar data protection regulations, our{" "}
        <Link to="/dpa" className={linkClass}>
          DPA
        </Link>{" "}
        applies and is incorporated into these Terms. You may request a signed copy by contacting {LEGAL_COMPANY.supportEmail}.
        Processing remains subject to these Terms, the Privacy Policy, and the DPA.
      </p>
      <p>
        <strong className="text-white">Feedback:</strong> If you provide Feedback, you grant {LEGAL_COMPANY.brandName} a royalty-free,
        worldwide, perpetual, irrevocable licence to use, modify, and incorporate such Feedback into our Service without
        any compensation or obligation to you.
      </p>
      <p>
        <strong className="text-white">Publicity Rights:</strong> {LEGAL_COMPANY.brandName} will not use your company name or logo as a
        customer reference in marketing materials without your prior written consent (including by email). You may
        grant or withdraw such consent by emailing {LEGAL_COMPANY.supportEmail}.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">8. Third-Party Services &amp; Integrations</h2>
      <p>
        Our Service integrates with third-party applications (e.g., Google Calendar, CRMs, email service providers).
        {LEGAL_COMPANY.brandName} is not responsible for the availability, uptime, or functionality of these third-party services. We are
        not liable for any missed bookings, delayed leads, or failed communications caused by third-party API outages
        or changes, except to the extent such liability cannot be excluded under applicable law.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">9. Disclaimer of Warranties &amp; Consumer Law Provisions</h2>
      <p>
        <strong className="text-white">General Disclaimer:</strong> To the maximum extent permitted by applicable law, the
        Service is provided on an &quot;AS IS&quot; and &quot;AS AVAILABLE&quot; basis. {LEGAL_COMPANY.brandName} disclaims all
        warranties, express or implied, including merchantability, fitness for a particular purpose, and
        non-infringement. We do not guarantee that the Service will be error-free, secure, or operate without
        interruption, and we expressly disclaim any binding Service Level Agreements (SLAs) regarding response speeds or
        uptime unless separately agreed in a signed written SLA.
      </p>
      <p>
        <strong className="text-white">Non-Excludable Rights:</strong> Nothing in these Terms excludes, restricts, or
        modifies any rights or remedies that cannot be excluded, restricted, or modified under applicable law, including
        non-excludable consumer guarantees and protections under the ACL, the CGA, the New Zealand Fair Trading Act
        1986, and unfair contract terms regimes (including the ACL unfair contract terms provisions and the New Zealand
        Fair Trading Act 1986 unfair contract terms provisions). Nothing in these Terms excludes liability for misleading
        or deceptive conduct where such exclusion is prohibited. If any provision of these Terms is found to be an
        unfair contract term under applicable law, it will be severed or modified to the minimum extent necessary.
      </p>
      <p>
        <strong className="text-white">Australian Consumer Law (ACL) — Limited Liability for Business Supplies:</strong>{" "}
        Where the ACL applies and {LEGAL_COMPANY.brandName}&apos; liability for failure to comply with a consumer guarantee in respect of
        services can lawfully be limited under ACL section 64A, {LEGAL_COMPANY.brandName}&apos; liability is limited to, at
        {LEGAL_COMPANY.brandName}&apos; option: (a) the resupply of the services; or (b) the payment of the cost of having the services
        resupplied.
      </p>
      <p>
        <strong className="text-white">New Zealand Consumer Guarantees Act (CGA):</strong> Where you acquire the Service
        for the purposes of a business (as contemplated by CGA section 43), you and {LEGAL_COMPANY.brandName} agree in writing that
        the guarantees and rights under the CGA are excluded to the maximum extent permitted by law. Where such
        exclusion is not permitted, nothing in these Terms limits non-excludable CGA rights.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">10. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by applicable law, in no event shall {LEGAL_COMPANY.legalName}, its directors, officers,
        employees, or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages,
        including lost profits, lost leads, or data loss, arising out of or related to your use of the Service.
      </p>
      <p>
        In no event shall our total aggregate liability for all claims arising out of or related to these Terms or the
        Service exceed the total amount you paid to {LEGAL_COMPANY.brandName} for the Service in the twelve (12) months preceding the
        claim (or the AUD equivalent of such amounts at the relevant time).
      </p>
      <p>
        The limitations in this section do not apply to: (a) liability that cannot be limited or excluded under
        applicable law (including non-excludable rights under the ACL or CGA); (b) fraud or fraudulent
        misrepresentation; (c) liability arising from gross negligence or wilful misconduct where such limitation is
        prohibited by law; or (d) death or personal injury to the extent such liability cannot be limited under applicable
        law.
      </p>
      <p>
        Where ACL section 64A applies as described in Section 9, {LEGAL_COMPANY.brandName}&apos; liability for the relevant consumer
        guarantee failure is limited as set out in Section 9.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">11. Indemnification</h2>
      <p>
        <strong className="text-white">Customer Indemnity:</strong> You agree to defend, indemnify, and hold harmless
        {LEGAL_COMPANY.legalName} and its officers, directors, employees, and agents from any third-party claims, damages,
        obligations, losses, liabilities, costs, or debt (including reasonable attorney&apos;s fees) arising from: (a)
        Customer Content and outbound communications you send or cause to be sent through the Service; (b) your material
        breach of these Terms giving rise to a third-party claim; (c) your violation of privacy, telemarketing, or
        communication compliance laws (including TCPA, CAN-SPAM, spam statutes, do-not-call rules, or A2P 10DLC
        obligations); and (d) claims by your leads or customers arising from your instructions, configurations, or Customer
        Content.
      </p>
      <p>
        <strong className="text-white">{LEGAL_COMPANY.brandName} IP Indemnity:</strong> {LEGAL_COMPANY.brandName} will defend you against any
        third-party claim that the unmodified Service, as provided by {LEGAL_COMPANY.brandName} and used by you in accordance with
        these Terms, infringes that third party&apos;s intellectual property rights, and will pay damages finally awarded
        against you (or agreed in settlement by {LEGAL_COMPANY.brandName}) arising from such claim. This indemnity does not apply to
        claims arising from: (i) Customer Content or combinations with products or services not provided by {LEGAL_COMPANY.brandName};
        (ii) modifications not made by {LEGAL_COMPANY.brandName}; (iii) use of Beta Features; or (iv) your use of the Service in breach
        of these Terms. If infringement is claimed, {LEGAL_COMPANY.brandName} may, at its option: procure the right for you to continue
        using the Service, modify the Service so it is non-infringing, or terminate the affected portion and provide a
        pro-rata refund of prepaid fees for the unused portion of the then-current billing period.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">12. Governing Law &amp; Dispute Resolution</h2>
      <p>
        <strong className="text-white">Governing Law:</strong> These Terms are governed by and construed in accordance
        with the laws of {LEGAL_COMPANY.governingLaw}, without regard to its conflict of law provisions.
      </p>
      <p>
        <strong className="text-white">Negotiation:</strong> Before commencing formal proceedings, either party must
        provide written notice of the dispute to the other (for {LEGAL_COMPANY.brandName}: {LEGAL_COMPANY.supportEmail}). The parties will
        attempt in good faith to resolve the dispute within thirty (30) days after notice.
      </p>
      <p>
        <strong className="text-white">Mediation:</strong> If the dispute remains unresolved after the negotiation period,
        the parties must refer the dispute to mediation in {LEGAL_COMPANY.governingLaw}, administered by an agreed
        mediator or, failing agreement within fourteen (14) days, a mediator nominated by the Australian Disputes Centre
        (or a successor body), before commencing court proceedings. Each party will bear its own costs of mediation unless
        otherwise agreed or ordered. Mediation is not required before: (a) seeking urgent interlocutory or injunctive
        relief; (b) recovering undisputed unpaid fees; or (c) exercising rights under the Statutory &amp; Regulatory
        Carve-Out below.
      </p>
      <p>
        <strong className="text-white">Courts:</strong> Subject to the negotiation and (where required) mediation steps
        above, the courts of {LEGAL_COMPANY.governingLaw} (and courts of appeal therefrom) have exclusive jurisdiction over
        disputes arising out of or relating to these Terms or the Service.
      </p>
      <p>
        <strong className="text-white">Statutory &amp; Regulatory Carve-Out:</strong> Nothing in this section prevents
        you from lodging a complaint with a relevant regulator (including the Australian Competition and Consumer
        Commission (ACCC), the Office of the Australian Information Commissioner (OAIC), the New Zealand Commerce
        Commission, or the Office of the Privacy Commissioner (NZ)), or from exercising non-excludable statutory rights
        in a competent forum where mandatory law requires.
      </p>
      <p>
        <strong className="text-white">Costs:</strong> Each party shall bear its own costs and attorney&apos;s fees in
        connection with a dispute, except as otherwise awarded by a court or agreed in writing.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">13. Termination &amp; Suspension</h2>
      <p>
        {LEGAL_COMPANY.brandName} may suspend or terminate your access to the Service: (a) immediately, if you engage in illegal use,
        pose a security risk, or engage in conduct that we reasonably believe creates material legal or operational risk;
        (b) for non-payment, after notice and a reasonable opportunity to cure payment failure; or (c) for other
        material breaches of these Terms, if the breach is capable of cure and remains uncured for ten (10) business
        days after written notice describing the breach.
      </p>
      <p>
        You may terminate these Terms by cancelling your subscription in accordance with Section 5 and ceasing use of
        the Service.
      </p>
      <p>
        Upon termination, your right to use the Service ceases at the end of the applicable access period (or immediately
        upon termination for cause under (a)). Sections pertaining to Intellectual Property, Disclaimer of Warranties,
        Limitation of Liability, Indemnification, Dispute Resolution, Confidentiality, and Survival shall survive
        termination.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">14. Confidentiality</h2>
      <p>
        Each party agrees to keep confidential any Confidential Information received from the other party in connection
        with the Service, and to use such information only for purposes of performing under these Terms. This obligation
        does not apply to information that is publicly available through no fault of the receiving party, independently
        developed without use of the discloser&apos;s Confidential Information, or required to be disclosed by law (in
        which case the receiving party will, where legally permitted, give reasonable prior notice).
      </p>
      <p>
        Upon termination or written request, each party will, within a reasonable time, return or securely destroy the
        other party&apos;s Confidential Information in its possession or control, except for copies retained as required
        by law, professional obligations, or automated backup systems (which remain subject to confidentiality until
        purged).
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">15. Electronic Communications</h2>
      <p>
        By creating an Account or using the Service, you consent to receive communications from {LEGAL_COMPANY.brandName} electronically,
        including agreements, notices, billing statements, and disclosures. You agree that electronic communications
        satisfy any legal requirement that such communications be in writing, consistent with the Electronic
        Transactions Act 1999 (Cth), the Electronic Transactions Act 2000 (NSW), and analogous electronic transactions
        laws in jurisdictions where you are located.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">16. Notices</h2>
      <p>Legal notices to {LEGAL_COMPANY.brandName} must be sent to:</p>
      <div className="pl-4 border-l border-white/10 space-y-1">
        <p>{LEGAL_COMPANY.legalName}</p>
        <p>ACN: {LEGAL_COMPANY.acn}</p>
        <p>ABN: {LEGAL_COMPANY.abn}</p>
        <p>{LEGAL_COMPANY.address}</p>
        <p>
          Email:{" "}
          <a href={`mailto:${LEGAL_COMPANY.supportEmail}`} className={linkClass}>
            {LEGAL_COMPANY.supportEmail}
          </a>
        </p>
      </div>
      <p>
        Notices to you will be sent to the email address associated with your Account or displayed in the Service.
        Notices are deemed received when sent to the designated email address or posted in the Service.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">17. Assignment</h2>
      <p>
        {LEGAL_COMPANY.brandName} may assign or transfer these Terms and its rights and obligations hereunder, in whole or in part, in
        connection with a merger, acquisition, reorganization, or sale of assets, with notice to you. You may not assign
        or transfer these Terms without {LEGAL_COMPANY.brandName}&apos; prior written consent, except to an affiliate or successor that
        assumes your obligations in writing and is not a direct competitor of {LEGAL_COMPANY.brandName}. Any attempted assignment in
        breach of this section is void.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">18. Service Modifications</h2>
      <p>
        {LEGAL_COMPANY.brandName} reserves the right to modify, update, or discontinue features of the Service. {LEGAL_COMPANY.brandName} will not
        materially reduce core paid functionality without reasonable prior notice via email or in-product notification.
        If a material change adversely reduces core paid functionality and you do not agree, you may cancel your
        subscription before the change takes effect in accordance with Section 5 and receive a pro-rata refund of any
        prepaid fees for the unused portion of your current billing period. No SLA regarding uptime, response speeds,
        or availability applies unless separately agreed in a signed written agreement.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">19. Security &amp; Data Breach Notification</h2>
      <p>
        {LEGAL_COMPANY.brandName} implements appropriate technical and organizational measures to protect your data. In the event of a
        personal data breach that compromises your information, we will notify you and relevant supervisory authorities
        without undue delay as required by applicable law,         consistent with our{" "}
        <Link to="/privacy-policy" className={linkClass}>
          Privacy Policy
        </Link>
        . For privacy complaints in Australia or New Zealand, please refer to the Complaints section of our Privacy
        Policy for contact details and regulatory authority information.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">20. Force Majeure</h2>
      <p>
        {LEGAL_COMPANY.brandName} shall not be liable for any failure or delay in performance due to circumstances beyond our reasonable
        control, including but not limited to acts of God, natural disasters, war, strikes, pandemics, government
        actions, internet network outages, or failures of third-party cloud hosting providers or underlying AI models
        (e.g., OpenAI, AWS).
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">
        21. No Waiver, Relationship, Severability, Language &amp; Third-Party Beneficiaries
      </h2>
      <p>
        <strong className="text-white">No Waiver:</strong> {LEGAL_COMPANY.brandName}&apos; failure to enforce any provision of these
        Terms shall not constitute a waiver of that provision or any other provision.
      </p>
      <p>
        <strong className="text-white">Relationship:</strong> Nothing in these Terms creates a partnership, joint venture,
        agency, or employment relationship between you and {LEGAL_COMPANY.brandName}. You have no authority to bind {LEGAL_COMPANY.brandName} in any
        manner.
      </p>
      <p>
        <strong className="text-white">Severability:</strong> If any provision of these Terms is found to be unenforceable
        or invalid, that provision will be limited or eliminated to the minimum extent necessary so that these Terms will
        otherwise remain in full force and effect and enforceable.
      </p>
      <p>
        <strong className="text-white">English Language:</strong> These Terms are prepared in English. The English
        version controls if these Terms are translated.
      </p>
      <p>
        <strong className="text-white">No Third-Party Beneficiaries:</strong> Except for {LEGAL_COMPANY.brandName}&apos; affiliates and
        the indemnified persons identified in Section 11, these Terms do not confer rights on any third party.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">22. Claims Period</h2>
      <p>
        To the maximum extent permitted by applicable law, any claim arising out of or relating to these Terms or the
        Service must be filed within twenty-four (24) months after the claim accrues, or it is permanently barred. This
        does not limit any longer period under the Limitation Act 1969 (NSW) or other mandatory law that cannot be
        shortened by contract.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">23. Entire Agreement, Order of Precedence &amp; Survival</h2>
      <p>
        These Terms, together with the Privacy Policy, the{" "}
        <Link to="/dpa" className={linkClass}>
          DPA
        </Link>{" "}
        (available at /dpa), and any signed Order Form or statement of work, constitute the entire agreement between you
        and {LEGAL_COMPANY.brandName} regarding the Service and supersede all prior agreements and understandings, whether written or oral.
      </p>
      <p>
        <strong className="text-white">Order of Precedence:</strong> In the event of a conflict, the following order of
        precedence applies: (1) a signed Order Form or statement of work prevails for commercial terms (pricing, term
        length, and scope); (2) the DPA prevails for data processing obligations (and if a signed copy of the DPA exists,
        that signed copy controls over the online DPA only where they conflict on the same topic); (3) these Terms prevail
        for all other matters; (4) the Privacy Policy governs personal data collection and use practices.
      </p>
      <p>
        Sections pertaining to Intellectual Property, Disclaimer of Warranties, Limitation of Liability,
        Indemnification, Dispute Resolution, Confidentiality, Claims Period, and Survival shall survive any termination
        or expiration of these Terms.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">24. Changes to Terms</h2>
      <p>
        {LEGAL_COMPANY.brandName} reserves the right to modify or replace these Terms at any time. We will provide notice of material
        changes via email or through the Service at least 30 days before the changes take effect, where practicable. If a
        material change adversely affects your rights, you may cancel your subscription before the change takes effect and
        receive a pro-rata refund of any prepaid fees for the unused portion of your current billing period, to the
        extent required by applicable law or as stated in this section. Your continued use of the Service following the
        effective date of any changes (after such notice period) constitutes acceptance of those changes.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">Contact Us</h2>
      <p>If you have any questions about these Terms, please contact us at:</p>
      <div className="pl-4 border-l border-white/10 space-y-1">
        <p>
          <span className="text-gray-500">Company:</span> {LEGAL_COMPANY.legalName}
        </p>
        <p>
          <span className="text-gray-500">ACN:</span> {LEGAL_COMPANY.acn}
        </p>
        <p>
          <span className="text-gray-500">ABN:</span> {LEGAL_COMPANY.abn}
        </p>
        <p>
          <span className="text-gray-500">Email:</span>{" "}
          <a href={`mailto:${LEGAL_COMPANY.supportEmail}`} className={linkClass}>
            {LEGAL_COMPANY.supportEmail}
          </a>
        </p>
        <p>
          <span className="text-gray-500">Address:</span> {LEGAL_COMPANY.address}
        </p>
      </div>
    </section>
  </LegalPageShell>
);

export default TermsPage;
